"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Field, Input, FormStatus, SubmitButton } from "@/components/ui/Form";
import { OAuthButtons } from "@/components/shared/OAuthButtons/OAuthButtons";
import { LogingovChooser, LogingovPendingLinker } from "@/components/shared/Logingov/Logingov";
import { AuthMfaModal } from "@/components/catalogs/CUserAccounts/AuthMfaModal";
import { useLocale, useT } from "@/lib/i18n/context";
import { useFrontendSession } from "@/lib/api/session-context";
import { submitSsoPlant } from "@/lib/sso/plant";
import type { WUserAccountContent } from "@/lib/api/cms-window-content";
import { wUserAccountTranslations, type WUserAccountTranslations } from "./WUserAccount.i18n";
import styles from "./WUserAccount.module.css";

/**
 * WUserAccount — widget user-account w headerze.
 *
 * **Stan logged-out:** przycisk "Zaloguj się" -> klik otwiera slide-in modal
 * z formą login (login+pass) + opcjonalnie OAuth buttons (gated z linked
 * c_user_accounts katalogu) + linki "Załóż konto" / "Zapomniałem hasła".
 *
 * **Stan logged-in:** trigger pokazuje avatar (miniaturka z `imp_user.avatar`
 * lub inicjały fallback) + imię -> klik otwiera dropdown menu (Twoje konto,
 * Wyloguj).
 *
 * **Click-outside** + **ESC** zamykają modal/dropdown. **Focus trap** w modal'u
 * (a11y: keyboard nav nie wycieka poza dialog).
 *
 * **Linki do c_user_accounts katalogu** budowane z `content.linked_catalog.symbol`
 * + aktualny locale: `/${locale}/${slug}`.
 *
 * **Error states** (backend nie mógł zbudować payloadu) — renderujemy info
 * dla admina, nie crash całego okna.
 */
export function WUserAccount({ content }: WUserAccountProps) {
  const t = useT(wUserAccountTranslations);
  /* SSR-resolved session z cookie `imperial_frontend_session` — `content.session`
     z backend REST jest ZAWSZE null (REST nie czyta tego cookie, używa Bearer).
     `useFrontendSession()` to client-side context dostarczany przez
     `FrontendSessionProvider` w PublicLayout (page.tsx pobiera przez
     `getFrontendSession()` z cookie). */
  const session = useFrontendSession();

  /* Defensywny guard - `content` jest typowane non-null, ale WindowRenderer
     forwarduje `response_window_data` które MOŻE być null gdy backend rzucił
     wyjątek per-okno w batch bootstrap'cie (cross-domain/cross-language guard).
     Ten przypadek jest co do zasady obsłużony FallbackMessage'em w
     `RenderContent` (meta.fetch_error), ale gdyby null tu dotarł - nie crashuj
     na `content.error`, tylko renderuj pustą sekcję (visitor) zamiast TypeError. */
  if (!content) {
    return null;
  }

  /* Error states z backendu — renderuje się WIDOCZNY message tylko gdy admin
     wbudowany w stronę z banner'em (w produkcji visitor widzi pustą sekcję). */
  if (content.error) {
    return <ErrorView error={content.error} message={content.error_message} t={t} />;
  }

  if (session !== null) {
    return <LoggedInWidget content={content} session={session} t={t} />;
  }

  return <LoggedOutWidget content={content} t={t} />;
}

export type WUserAccountProps = {
  /** Null gdy backend rzucił wyjątek per-okno w batch bootstrap'cie (zob. guard
   *  w komponencie). Happy-path zawsze dostaje wypełniony obiekt. */
  content: WUserAccountContent | null;
};

/* ─── Error view ─────────────────────────────────────────────────────── */

function ErrorView({
  error,
  message,
  t,
}: {
  error: NonNullable<WUserAccountContent["error"]>;
  message: string | undefined;
  t: WUserAccountTranslations;
}) {
  const errorText =
    error === "window_not_linked"
      ? t.errorNotLinked
      : error === "linked_catalog_missing"
        ? t.errorCatalogMissing
        : t.errorFetchFailed;

  return (
    <div className={styles.errorState} role="alert">
      <strong>{errorText}</strong>
      {message && (
        <p style={{ marginTop: 8, fontFamily: "monospace", fontSize: "0.75rem" }}>
          {message}
        </p>
      )}
    </div>
  );
}

/* ─── Logged-in widget (avatar + dropdown) ───────────────────────────── */

function LoggedInWidget({
  content,
  session,
  t,
}: {
  content: WUserAccountContent;
  session: import("@/lib/api/session").FrontendUserSession;
  t: WUserAccountTranslations;
}) {
  const router = useRouter();
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  /* Avatar URL pobierany client-side z `/api/iiapi/user` — analogicznie do
     CUserAccounts ProfileTabs (route handler forwarduje cookie + zwraca
     pre-computed `avatar_sq_url` z `IUSER::getData`). Backend `getApiContent`
     w window context NIE ma Bearer tokenu, więc nie zna avatara. */
  const [avatarSqUrl, setAvatarSqUrl] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/iiapi/user", { cache: "no-store" });
        if (!res.ok) return;
        const body = (await res.json().catch(() => null)) as {
          profile?: { avatar_sq_url?: string | null };
        } | null;
        if (cancelled) return;
        setAvatarSqUrl(body?.profile?.avatar_sq_url ?? null);
      } catch {
        /* Network error — fallback do inicjałów (avatarSqUrl=null). */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const user = {
    id: session.user.id,
    login: session.user.login,
    name: session.user.name,
    email: session.user.email,
  };
  const catalogSlug = content.linked_catalog?.symbol;
  const myAccountHref = catalogSlug ? `/${locale}/${catalogSlug}/` : null;

  /* Click-outside + ESC zamykają dropdown. */
  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleLogout = useCallback(() => {
    if (logoutBusy) return;
    setLogoutBusy(true);
    setLogoutError(null);
    void (async () => {
      try {
        const res = await fetch("/api/iiapi/logout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.message ?? t.logoutFailed);
        }
        setOpen(false);
        router.refresh();
      } catch (e) {
        setLogoutError(e instanceof Error ? e.message : String(e));
        setLogoutBusy(false);
      }
    })();
  }, [logoutBusy, router, t]);

  const displayName = user.name || user.login || t.defaultGreetingName;

  return (
    <div className={styles.root} ref={rootRef}>
      {/* Dokończenie powiązania z login.gov.pl po zalogowaniu hasłem (wybór "połącz"). */}
      <LogingovPendingLinker />
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        {avatarSqUrl ? (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            className={styles.avatar}
            src={avatarSqUrl}
            alt=""
            aria-hidden="true"
          />
        ) : (
          <span className={styles.avatarInitials} aria-hidden="true">
            {getInitials(displayName)}
          </span>
        )}
        <span className={styles.triggerLabel}>{displayName}</span>
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          <div className={styles.dropdownHeader}>
            <p className={styles.dropdownGreeting}>
              {t.greeting.replace("{name}", displayName)}
            </p>
            {user.email && <p className={styles.dropdownEmail}>{user.email}</p>}
          </div>

          {myAccountHref && (
            <Link
              href={myAccountHref}
              className={styles.dropdownItem}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              {t.myAccountLink}
            </Link>
          )}

          <button
            type="button"
            className={styles.dropdownItem}
            onClick={handleLogout}
            disabled={logoutBusy}
            role="menuitem"
          >
            {logoutBusy ? `${t.logoutAction}…` : t.logoutAction}
          </button>

          {logoutError && (
            <div style={{ padding: "0 16px 8px" }}>
              <FormStatus variant="error">{logoutError}</FormStatus>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Logged-out widget (button -> modal) ─────────────────────────────── */

function LoggedOutWidget({
  content,
  t,
}: {
  content: WUserAccountContent;
  t: WUserAccountTranslations;
}) {
  const [open, setOpen] = useState(false);

  /* Powrót z login.gov.pl osoby bez konta (`?logingov=choose`) na dowolną stronę:
     modal otwiera się sam z wyborem "załóż konto" / "połącz". Na stronie katalogu
     kont wybór pokazuje sam katalog - drugi, w modalu, byłby duplikatem. Stan
     wyliczany z adresu (bez setState w efekcie); zamknięcie modalu zapamiętuje
     odrzucenie wyboru na czas życia komponentu. */
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const locale = useLocale();
  const catalogSlug = content.linked_catalog?.symbol;
  const [chooserDismissed, setChooserDismissed] = useState(false);
  const chooserPending =
    searchParams.get("logingov") === "choose"
    && !(catalogSlug && pathname.startsWith(`/${locale}/${catalogSlug}`))
    && !chooserDismissed;
  const modalOpen = open || chooserPending;
  const closeModal = useCallback(() => {
    setOpen(false);
    setChooserDismissed(true);
  }, []);

  /* ESC zamyka modal. Body scroll lock gdy open. */
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    /* Prevent body scroll while modal open (focus on dialog). */
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [modalOpen, closeModal]);

  /* Global "open login modal" event - konsumenci spoza WUserAccount (np. CShop
     tab "Zaloguj się" przy zamówieniu z konta) dispatchują `wuseraccount:openlogin`
     zamiast renderować własny inline form. Jedna forma logowania w calej aplikacji. */
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("wuseraccount:openlogin", onOpen);
    return () => window.removeEventListener("wuseraccount:openlogin", onOpen);
  }, []);

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <svg
          className={styles.loginIcon}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
        <span className={styles.triggerLabel}>{t.loginButton}</span>
      </button>

      {modalOpen && <LoginModal content={content} t={t} onClose={closeModal} />}
    </div>
  );
}

/* ─── Modal z formą login ────────────────────────────────────────────── */

type LoginFormValues = {
  login: string;
  password: string;
};

function buildLoginSchema(t: WUserAccountTranslations) {
  return z.object({
    login: z.string().min(1, t.validationLoginRequired),
    password: z.string().min(1, t.validationPasswordRequired),
  });
}

function LoginModal({
  content,
  t,
  onClose,
}: {
  content: WUserAccountContent;
  t: WUserAccountTranslations;
  onClose: () => void;
}) {
  const router = useRouter();
  const locale = useLocale();
  const loginSchema = buildLoginSchema(t);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    /* `onSubmit` (nie `onTouched`) — przy `onTouched` klik na "Załóż konto" /
       "Zapomniałem hasła" wewnątrz formularza powodował blur focused inputu
       -> triggerował walidację -> user widział czerwone "Podaj login lub
       adres email" zamiast navigacji do odpowiedniego formularza. */
    mode: "onSubmit",
    defaultValues: { login: "", password: "" },
  });
  const [topError, setTopError] = useState<string | null>(null);

  const catalogSlug = content.linked_catalog?.symbol;
  const accountHref = catalogSlug ? `/${locale}/${catalogSlug}/` : null;
  const registerHref = accountHref ? `${accountHref}?action=register` : null;
  const forgotHref = accountHref ? `${accountHref}?action=remind-password` : null;

  /* MFA modal state - analog CUserAccounts LoginForm. */
  const [showMfaModal, setShowMfaModal] = useState(false);

  //Wspolna finalizacja post-login (SSO plant + close + refresh) - reuse przez
  //normalny login i sukces AuthMfaModal.
  const finishLoginRedirect = (plant?: { ticket: string; action: string }) => {
    if (plant?.ticket && plant?.action && submitSsoPlant(plant.action, plant.ticket)) {
      return;
    }
    onClose();
    router.refresh();
  };

  const onSubmit = async (values: LoginFormValues) => {
    setTopError(null);
    try {
      const res = await fetch("/api/iiapi/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          login: values.login.trim(),
          password: values.password,
        }),
      });
      const body = (await res.json().catch(() => null)) as {
        ok?: boolean;
        message?: string;
        errors?: Record<string, string>;
        plant?: { ticket: string; action: string };
        mfa_required?: boolean;
      } | null;

      if (!res.ok) {
        if (body?.errors) {
          for (const [name, msg] of Object.entries(body.errors)) {
            if (typeof msg === "string") {
              setError(name as keyof LoginFormValues, { type: "server", message: msg });
            }
          }
          return;
        }
        setTopError(body?.message ?? t.loginFailed);
        return;
      }

      //MFA required - otwieramy modal, cookie challenge juz ustawiony przez
      //route handler. Success modal -> finishLoginRedirect (SSO + close +
      //refresh wspolnie z direct login).
      if (body?.mfa_required === true) {
        setShowMfaModal(true);
        return;
      }

      finishLoginRedirect(body?.plant);
    } catch (e) {
      setTopError(e instanceof Error ? e.message : t.networkError);
    }
  };

  /* OAuth gating analogiczny do CUserAccounts — 3 poziomy:
       (1) rozszerzenie systemowe enabled (backend zawarte w auth_providers.*.enabled)
       (2) aktywna integracja (j.w.)
       (3) per-katalog admin toggle (content.config.enable_*_login)
     Frontend kombinuje (1+2) AND (3). */
  const showOAuth = content.auth_providers !== null;
  const effectiveProviders = content.auth_providers
    ? {
        facebook: {
          enabled:
            content.auth_providers.facebook.enabled && content.config.enable_facebook_login,
          app_id: content.auth_providers.facebook.app_id,
        },
        google: {
          enabled:
            content.auth_providers.google.enabled && content.config.enable_google_login,
          client_id: content.auth_providers.google.client_id,
        },
        logingov: {
          enabled:
            !!content.auth_providers.logingov?.enabled && !!content.config.enable_logingov_login,
          login_url: content.auth_providers.logingov?.login_url ?? null,
        },
      }
    : null;

  return (
    <>
      {showMfaModal && (
        <AuthMfaModal
          onSuccess={(result) => {
            setShowMfaModal(false);
            finishLoginRedirect(result.plant);
          }}
          onCancel={() => setShowMfaModal(false)}
        />
      )}
      <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wuseraccount-modal-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="wuseraccount-modal-title" className={styles.modalTitle}>
            {t.modalTitle}
          </h2>
          <button
            type="button"
            className={styles.modalClose}
            onClick={onClose}
            aria-label={t.modalClose}
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <LogingovChooser />
          <form className={styles.loginForm} onSubmit={handleSubmit(onSubmit)} noValidate>
            <Field label={t.loginLabel} required error={errors.login?.message}>
              <Input
                type="text"
                autoComplete="username"
                autoFocus
                disabled={isSubmitting}
                {...register("login")}
              />
            </Field>

            <Field label={t.passwordLabel} required error={errors.password?.message}>
              <Input
                type="password"
                autoComplete="current-password"
                disabled={isSubmitting}
                {...register("password")}
              />
            </Field>

            {topError && <FormStatus variant="error">{topError}</FormStatus>}

            <SubmitButton
              loading={isSubmitting}
              loadingLabel={t.submitLoginBusy}
            >
              {t.submitLogin}
            </SubmitButton>
          </form>

          {/* Linki POZA `<form>` — wewnątrz formularza klik powodował blur
              inputu + interakcję z react-hook-form'em, co rozjeżdżało navigację
              i pokazywało walidację. Sibling formularza w `modalBody` (flex col)
              zachowuje identyczny layout. */}
          <div className={styles.formLinks}>
            {content.config.user_registration_enabled && registerHref && (
              <Link href={registerHref} onClick={onClose}>
                {t.registerLink}
              </Link>
            )}
            {forgotHref && (
              <Link href={forgotHref} onClick={onClose}>
                {t.forgotPasswordLink}
              </Link>
            )}
          </div>

          {showOAuth && effectiveProviders && (
            <OAuthButtons
              providers={effectiveProviders}
              userGroups={content.user_groups}
              sLang={locale}
            />
          )}
        </div>
      </div>
    </>
  );
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

/** Pierwsze litery imienia/nazwiska (max 2 znaki) dla avatar fallback. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] + parts[parts.length - 1][0]).slice(0, 2);
}
